/**
 * How a measure is RENDERED — the presentation layer over `measures.ts`.
 *
 * `measures.ts` answers "what does this unit mean, and may these two amounts be
 * added together". This file answers "what should a human read". They are kept
 * apart on purpose: the vocabulary is incident-hardened and semantics-only,
 * while presentation policy (ladders, term counts, fraction tables, rounding)
 * churns with taste. A formatting tweak should never be a diff against the file
 * the fleet's arithmetic correctness depends on.
 *
 * Lives in `@ordo/constants` — zero-dependency and browser-safe — because both
 * the ordo React client and server-side document rendering (the printed prep
 * packets) must produce the same string for the same amount.
 *
 * Two rendering contexts, and the difference is not cosmetic:
 *
 *   PURCHASING — one unit, no fractions. You BUY by weight, so a purchase line
 *     reads "13.2 lb". Splitting that into "13 lb + 3 oz" re-fragments exactly
 *     the number the 2026-08-06 Classic Catering incident was about getting
 *     right. This is the DEFAULT, so an un-migrated call site cannot silently
 *     acquire compound rendering.
 *
 *   COOKING — up to two terms, fractions on. A cook measuring into a pot wants
 *     "2 cups + 2 tbsp", not "2.13 cups".
 *
 * Every ladder factor is DERIVED from `measures.ts` at module load rather than
 * retyped, so a second numeric table cannot drift into existence. That drift is
 * what this whole area of the codebase has been paying for.
 */
import { type MeasureDimension, measureDimension, normalizeMeasureUnit, volumeToCups, weightToGrams } from './measures'

/** Which unit family a chef reads amounts in. `as-authored` leaves them alone. */
export type MeasureSystem = 'imperial' | 'metric' | 'as-authored'

/** Why the amount is on screen. Drives term count, fractions, and rounding. */
export type MeasureUse = 'COOKING' | 'PURCHASING'

/**
 * A unit string that has been through display conversion.
 *
 * Branded so it CANNOT be passed where an authored unit is expected — a
 * converted unit reaching `ingredientKeyFor` or a substitution payload would
 * write a fabricated unit into the database permanently. See the `AuthoredUnit`
 * counter-brand in `apps/ordo/client/src/lib/chefs/substitutions.ts`; the two
 * brands are deliberately incompatible in both directions.
 */
export type DisplayUnit = string & { readonly __unitOrigin: 'display' }

export interface DisplayTerm {
  readonly value: number
  readonly unit: DisplayUnit
  /** This term alone, e.g. "2 cups". */
  readonly text: string
}

export interface DisplayMeasure {
  /** The whole label, e.g. "2 cups + 2 tbsp". Safe to render; useless as a key. */
  readonly text: string
  readonly terms: readonly DisplayTerm[]
  readonly dimension: MeasureDimension
}

export interface FormatMeasureOptions {
  /** Default `'as-authored'` — never convert unless asked. */
  system?: MeasureSystem
  /** Default `'PURCHASING'` — the conservative, single-unit rendering. */
  use?: MeasureUse
  /** Override the use-derived term cap. Ignored for PURCHASING (always 1). */
  maxTerms?: number
  /** Override the use-derived fraction policy. Ignored for metric (always off). */
  fractions?: boolean
}

// ---------------------------------------------------------------------------
// Ladders — derived, never retyped
// ---------------------------------------------------------------------------

type Ladder = ReadonlyArray<readonly [string, number]>

/** Build a descending ladder, pulling each factor from the shared vocabulary. */
function ladder(units: readonly string[], pivot: (unit: string) => number | null): Ladder {
  return units.map((unit) => {
    const factor = pivot(unit)
    if (factor === null || !Number.isFinite(factor) || factor <= 0) {
      // A typo in the unit list is a build-time-ish bug, not a runtime fallback.
      throw new Error(`measure-format: "${unit}" is not a recognized unit in measures.ts`)
    }
    return [unit, factor] as const
  })
}

const toGrams = (unit: string): number | null => weightToGrams(1, unit)
const toCups = (unit: string): number | null => volumeToCups(1, unit)

const IMPERIAL_WEIGHT = ladder(['lb', 'oz'], toGrams)
const METRIC_WEIGHT = ladder(['kg', 'g'], toGrams)
const IMPERIAL_VOLUME = ladder(['gal', 'qt', 'cup', 'tbsp', 'tsp'], toCups)
const METRIC_VOLUME = ladder(['l', 'ml'], toCups)

/** Ladder units whose display spelling differs from their lookup key. */
const DISPLAY_SPELLING: Record<string, string> = { l: 'L' }

/** Ladder units that take a plural above one. "3/4 cup" but "2 cups". */
const PLURAL: Record<string, string> = { cup: 'cups' }

/** Metric units are written decimally. "2 1/2 g" is not a thing anyone writes. */
const METRIC_UNITS = new Set(['g', 'kg', 'mg', 'ml', 'l'])

function isMetricUnit(unit: string): boolean {
  return METRIC_UNITS.has(normalizeMeasureUnit(unit))
}

function ladderFor(dimension: MeasureDimension, system: MeasureSystem): Ladder | null {
  if (system === 'as-authored') {
    return null
  }
  const metric = system === 'metric'
  if (dimension === 'WEIGHT') {
    return metric ? METRIC_WEIGHT : IMPERIAL_WEIGHT
  }
  if (dimension === 'VOLUME') {
    return metric ? METRIC_VOLUME : IMPERIAL_VOLUME
  }
  return null
}

// ---------------------------------------------------------------------------
// Number rendering
// ---------------------------------------------------------------------------

/**
 * Fractions a cook actually reads off a measuring cup.
 *
 * Spelled out in FULL-SIZE characters, deliberately, after three attempts at a
 * typographic fraction were rejected on the floor:
 *
 *   - a superscript/slash/subscript figure, at two different sets of
 *     proportions. To read as one fraction rather than three characters the
 *     digits have to shrink to roughly 0.6em, and at the 12px of a muted
 *     deliverables sublabel that is simply too small to read;
 *   - the font's own vulgar glyphs (¼ ⅓ ½ ⅔ ¾). Typographically the best of
 *     the three, and still the least legible thing in the row: Outfit draws
 *     their numerals at about 40% of cap height, so "½ Cup" reads lighter and
 *     smaller than the "1 Slice" directly above it.
 *
 * Legibility and typography genuinely pull against each other here, and in a
 * production kitchen legibility wins. "1/2" matches the visual weight of every
 * other amount on the row — "1 Slice", "4.5 oz", "6 fl oz" — which is the
 * property that actually matters when a cook is scanning a list.
 *
 * Do not "improve" this into glyphs or markup without putting it in front of a
 * kitchen first.
 */
const FRACTIONS: ReadonlyArray<readonly [number, string]> = [
  [0.25, '1/4'],
  [1 / 3, '1/3'],
  [0.5, '1/2'],
  [2 / 3, '2/3'],
  [0.75, '3/4'],
]

/** How close a value must sit to a fraction (or a whole number) to snap to it. */
const FRACTION_TOLERANCE = 0.02

/**
 * Render a value as a vulgar fraction when it lands near one ("3/4", "1 1/2"),
 * else as a decimal. Moved here from `apps/ordo/client/src/lib/chefs/format.ts`
 * (`formatServingAmount`) — the existing, tested precedent for cook-friendly
 * numbers, tolerance table and all.
 */
export function formatFractionalValue(value: number): string {
  const rounded = Math.round(value * 100) / 100
  const whole = Math.floor(rounded)
  const fraction = rounded - whole
  // Within a hair of a whole number: say the whole number. 0.99 oz is 1 oz to
  // anyone standing at a scale, and "0.99" reads as false precision.
  //
  // NEVER when that whole number is zero and the amount is not: 0.01 oz is a
  // real quantity, and rendering it "0 oz" tells a cook to use none of it.
  // A tiny amount stays small rather than disappearing.
  if (fraction < FRACTION_TOLERANCE && (whole > 0 || value === 0)) {
    return String(whole)
  }
  if (fraction > 1 - FRACTION_TOLERANCE) {
    return String(whole + 1)
  }
  const match = FRACTIONS.find(([candidate]) => Math.abs(fraction - candidate) < FRACTION_TOLERANCE)
  if (match) {
    return whole > 0 ? `${whole} ${match[1]}` : match[1]
  }
  if (rounded === 0 && value !== 0) {
    // Same rule as the decimal path: an amount the recipe calls for must
    // never read as "0". Too small for a fraction, so show it plainly.
    return value.toPrecision(1)
  }
  return String(rounded)
}

/**
 * Decimal rendering: 2dp with trailing zeros trimmed, thousands separated.
 * `12.5` → "12.5" (not "12.50"), `3400.4` → "3,400.4", `0.333` → "0.33".
 *
 * This is the behaviour of `api/shopping-list.ts`'s formatter — the most
 * defensible of the seven that existed before consolidation.
 */
export function formatMeasureValue(value: number, opts: { fractions?: boolean } = {}): string {
  if (!Number.isFinite(value)) {
    return '0'
  }
  if (opts.fractions) {
    return formatFractionalValue(value)
  }
  const rounded = Math.round(value * 100) / 100
  if (rounded === 0 && value !== 0) {
    // Two decimals would erase it. A quantity the recipe actually calls for
    // must never read as "0" — that instructs a cook to use none of it. Keep
    // whatever precision it takes to stay truthful.
    return value.toPrecision(1)
  }
  return rounded.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

/** Is `value` within tolerance of a whole number or a renderable fraction? */
function snapsToFraction(value: number): boolean {
  const fraction = value - Math.floor(value)
  if (fraction < FRACTION_TOLERANCE || fraction > 1 - FRACTION_TOLERANCE) {
    return true
  }
  return FRACTIONS.some(([candidate]) => Math.abs(fraction - candidate) < FRACTION_TOLERANCE)
}

/**
 * The number a reader will actually see, as a number.
 *
 * Carry detection has to compare against what gets DISPLAYED, not the exact
 * value — a tail of 0.999 cups renders as "1 cup" and must roll into the head.
 * Computed numerically rather than by parsing the formatted string, because
 * "1 1/2" does not parse back to 1.5.
 */
function displayedValue(value: number, fractions: boolean): number {
  const rounded = Math.round(value * 100) / 100
  if (!fractions) {
    return rounded
  }
  const whole = Math.floor(rounded)
  const fraction = rounded - whole
  const match = FRACTIONS.find(([candidate]) => Math.abs(fraction - candidate) < FRACTION_TOLERANCE)
  return match ? whole + match[0] : rounded
}

/**
 * True when the target system has no unit small enough to express this amount
 * usefully — 2 g of garlic is 0.07 oz, which tells a cook nothing.
 *
 * A conversion that destroys the information is not worth doing, so callers
 * fall back to the authored unit instead. The threshold is a quarter of the
 * finest rung: the smallest division anyone measures.
 */
function convertionWouldVanish(total: number, rungs: Ladder): boolean {
  const finest = rungs[rungs.length - 1]![1]
  return total < finest * 0.25
}

/**
 * Largest ladder unit that `total` fills at least once, else the smallest.
 * The epsilon keeps float drift from rendering exactly 16 oz as "15.99 oz".
 */
function pickHead(total: number, rungs: Ladder): readonly [string, number] {
  for (const rung of rungs) {
    if (total >= rung[1] * (1 - 1e-9)) {
      return rung
    }
  }
  return rungs[rungs.length - 1]!
}

/**
 * Restate an amount in the target system WITHOUT formatting it, picking the
 * largest sensible unit (7.5 lb, not 120 oz).
 *
 * Deliberately named and shaped differently from `measures.ts`'s
 * `convertMeasure(value, from, to)` — scalar in, object out — so the two can
 * never be confused at a call site. Count-style units pass through untouched.
 */
export function toDisplaySystem(
  value: number,
  unit: string,
  system: MeasureSystem,
): { value: number; unit: DisplayUnit } {
  const dimension = measureDimension(unit)
  const rungs = ladderFor(dimension, system)
  if (rungs === null) {
    return { value, unit: unit as DisplayUnit }
  }
  const pivot = dimension === 'WEIGHT' ? weightToGrams(value, unit) : volumeToCups(value, unit)
  if (pivot === null || !Number.isFinite(pivot)) {
    return { value, unit: unit as DisplayUnit }
  }
  if (convertionWouldVanish(Math.abs(pivot), rungs)) {
    return { value, unit: unit as DisplayUnit }
  }
  const [headUnit, headFactor] = pickHead(Math.abs(pivot), rungs)
  return { value: pivot / headFactor, unit: rungLabel(headUnit, pivot / headFactor) as DisplayUnit }
}

// ---------------------------------------------------------------------------
// The formatter
// ---------------------------------------------------------------------------

/** `label` is used verbatim — ladder callers pass the plural, passthrough
 *  callers pass the AUTHORED spelling so an un-converted unit is never
 *  silently reworded. */
function term(value: number, label: string, fractions: boolean): DisplayTerm {
  const text = `${formatMeasureValue(value, { fractions })} ${label}`
  return { value, unit: label as DisplayUnit, text }
}

function single(value: number, label: string, dimension: MeasureDimension, fractions: boolean): DisplayMeasure {
  // Fraction style follows the unit actually on screen, not the system that
  // was requested. An imperial reader whose amount fell back to grams (2 g of
  // garlic has no usable ounce figure) still reads "2.5 g", never "2 1/2 g".
  const only = term(value, label, fractions && !isMetricUnit(label))
  return { text: only.text, terms: [only], dimension }
}

/**
 * Emit a single term that came off a LADDER (i.e. we converted). Applies cook
 * granularity in one place so it cannot be applied in some branches and
 * forgotten in others — which is how "2.03 tbsp" survived the first pass.
 *
 * Deliberately not used for passthrough (COUNT / as-authored): quantizing
 * there would rewrite a number the author typed.
 */
function ladderSingle(value: number, unit: string, dimension: MeasureDimension, fractions: boolean): DisplayMeasure {
  const shown = fractions ? roundToMeasurable(value, dimension) : value
  return single(shown, rungLabel(unit, shown), dimension, fractions)
}

/** Ladder unit → the spelling a chef reads, pluralized above one. */
function rungLabel(unit: string, value: number): string {
  const base = DISPLAY_SPELLING[unit] ?? unit
  return value > 1 ? (PLURAL[unit] ?? base) : base
}

/**
 * Granularity a cook can actually measure.
 *
 * VOLUME quantizes to quarters: measuring cups and spoons are discrete
 * objects, so "2.03 tbsp" is a number rather than an instruction — you cannot
 * pour three hundredths of a tablespoon.
 *
 * WEIGHT does NOT quantize. A scale is continuous and reads what it reads;
 * rounding 4.59 oz to 4 1/2 oz would throw away 2 g on a 130 g portion to no
 * one's benefit. This is the same reason volume promotes to a larger rung on a
 * clean fraction and weight does not.
 */
function roundToMeasurable(value: number, dimension: MeasureDimension): number {
  return dimension === 'VOLUME' ? Math.round(value * 4) / 4 : value
}

/**
 * Render an amount for a human.
 *
 * @param value  the quantity, in `unit`
 * @param unit   the AUTHORED unit — never a previously-converted one
 */
export function formatMeasure(value: number, unit: string, opts: FormatMeasureOptions = {}): DisplayMeasure {
  const system = opts.system ?? 'as-authored'
  const use = opts.use ?? 'PURCHASING'
  const dimension = measureDimension(unit)

  if (!Number.isFinite(value)) {
    return single(0, unit, dimension, false)
  }

  // COUNT is a tally of discrete things. It has no ladder and no conversion:
  // "Bowlpak (1 oz)" is one bowlpak, and the embedded oz is descriptive text
  // that must never be parsed into a dimension.
  if (dimension === 'COUNT') {
    return single(value, unit, dimension, use === 'COOKING')
  }

  const metric = system === 'metric'
  // Metric is decimal by construction. "1 kg + 250 g" and "1 1/4 kg" are both
  // wrong for a metric cook; 1.25 kg is what they read.
  const fractions = metric ? false : (opts.fractions ?? use === 'COOKING')
  const maxTerms = use === 'PURCHASING' || metric ? 1 : Math.max(1, opts.maxTerms ?? 2)

  const rungs = ladderFor(dimension, system)
  if (rungs === null) {
    return single(value, unit, dimension, fractions)
  }
  const pivot = dimension === 'WEIGHT' ? weightToGrams(value, unit) : volumeToCups(value, unit)
  if (pivot === null || !Number.isFinite(pivot) || pivot <= 0) {
    return single(value, unit, dimension, fractions)
  }
  // Sub-threshold amounts keep their authored unit rather than becoming 0.07 oz.
  if (convertionWouldVanish(pivot, rungs)) {
    return single(value, unit, dimension, fractions)
  }

  let [headUnit, headFactor] = pickHead(pivot, rungs)

  // Volume-only promotion: measuring cups come in quarters, scales do not. A
  // cook reads "3/4 cup", never "12 tbsp" — but they DO read "12 oz" rather
  // than "3/4 lb", because they weigh it.
  //
  // Only fires when the chosen rung needs an awkward COUNT of small units
  // (>= 4) and the rung above lands on a clean fraction. Without the count
  // gate this also rewrites 8 cups as "1/2 gal", which is true but not how
  // anyone cooks.
  if (fractions && dimension === 'VOLUME' && pivot / headFactor >= 4) {
    const above = rungs[rungs.findIndex(([u]) => u === headUnit) - 1]
    if (above !== undefined) {
      const promoted = pivot / above[1]
      if (promoted >= 0.25 && snapsToFraction(promoted)) {
        ;[headUnit, headFactor] = above
      }
    }
  }

  const headValue = pivot / headFactor

  if (maxTerms === 1) {
    return ladderSingle(headValue, headUnit, dimension, fractions)
  }

  // A fraction beats a second term: "2 1/2 cups" reads better than
  // "2 cups + 8 tbsp", so compound exists only for amounts a fraction cannot
  // express. This also catches 1.0001 → "1 cup", so no zero tail is emitted.
  if (fractions && snapsToFraction(headValue)) {
    return ladderSingle(headValue, headUnit, dimension, fractions)
  }

  const headWhole = Math.floor(headValue)
  const headIndex = rungs.findIndex(([u]) => u === headUnit)
  const tailRungs = rungs.slice(headIndex + 1)
  const remainder = pivot - headWhole * headFactor

  // Nothing worth a second term: below one head unit, already at the finest
  // rung, or the remainder is small enough that naming it is noise. At batch
  // scale "4 gal + 6 tbsp" is not a more useful instruction than "4 gal" —
  // the tail is 2% of the head and no one measures it into a stockpot.
  if (headWhole < 1 || tailRungs.length === 0 || remainder < headFactor * 0.05) {
    return ladderSingle(headValue, headUnit, dimension, fractions)
  }

  // The tail re-runs the head pick on the REMAINDER rather than stepping one
  // rung down, so 1.1 gal gives "1 gal + 1 1/2 cups", not "1 gal + 0.4 qt".
  const [tailUnit, tailFactor] = pickHead(remainder, tailRungs)
  const tailValue = fractions ? roundToMeasurable(remainder / tailFactor, dimension) : remainder / tailFactor
  const shownTail = displayedValue(tailValue, fractions)

  // Carry: a tail that DISPLAYS as a full head unit must not render as
  // "1 qt + 4 cups" — roll it into the head instead.
  if (shownTail * tailFactor >= headFactor * (1 - 1e-9)) {
    return ladderSingle(headWhole + 1, headUnit, dimension, fractions)
  }
  if (shownTail <= 0) {
    return ladderSingle(headValue, headUnit, dimension, fractions)
  }

  const head = term(headWhole, rungLabel(headUnit, headWhole), fractions)
  const tail = term(tailValue, rungLabel(tailUnit, tailValue), fractions)
  return { text: `${head.text} + ${tail.text}`, terms: [head, tail], dimension }
}

/** {@link formatMeasure}, when you only want the label. */
export function formatMeasureText(value: number, unit: string, opts: FormatMeasureOptions = {}): string {
  return formatMeasure(value, unit, opts).text
}

/**
 * The smallest volume worth showing a cook: a quarter teaspoon. Measuring
 * spoons stop there, so anything below reads better as the authored weight —
 * "0.5 g salt" is an instruction; "0.002 cup" is not.
 */
const MIN_USABLE_CUPS = 0.25 / 48

/**
 * Restate a WEIGHT amount in cups using a per-ingredient density, or null to
 * keep the weight. Null when the amount is not a weight, there is no density,
 * or the resulting volume is too small to measure.
 *
 * Lives here — not in the schema package — because the ordo client renders
 * recipe panels in the browser and must apply the same conversion the server
 * derived the density for. `gramsPerCup` comes from `IngredientDensity`
 * (USDA CNDB, or a hand-set override), delivered on the recipe wire.
 */
export function cupsFromWeight(value: number, unit: string, gramsPerCup: number | null | undefined): number | null {
  if (gramsPerCup === null || gramsPerCup === undefined || !Number.isFinite(gramsPerCup) || gramsPerCup <= 0) {
    return null
  }
  if (!Number.isFinite(value) || value <= 0) {
    return null
  }
  const grams = weightToGrams(value, unit)
  if (grams === null || grams <= 0) {
    return null
  }
  const cups = grams / gramsPerCup
  return cups >= MIN_USABLE_CUPS ? cups : null
}
